import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import React from 'react';

import type {OrderLicense} from 'app/components/OrderImageryModal/orderImageryUtils';
import type {CartCharges} from 'app/modules/Cart';
import * as conversionUtils from 'app/utils/conversionUtils';
import {formatCost} from 'app/utils/costUtils';

import type {BillingNoteField, SubmitBlock} from '../types';
import cs from './styles.module.scss';

const BILLING_NOTE_ID = 'cart-billing-note';
// Stripe's limit on the custom field the billing note is printed in.
const CHECKOUT_NOTE_MAX_LENGTH = 140;

export interface OrderSummarySidebarProps {
  itemCount: number;
  totalCents: number;
  totalAcres: number;
  isSubmitting: boolean;
  /** Non-null disables Submit and says why. */
  submitBlock: SubmitBlock | null;
  /** The card picker, when the cart charges at checkout; null when it bills a contract. */
  paymentSection: React.ReactNode;
  /** The note for the card checkout's invoice; null when the cart bills a contract. */
  billingNote: BillingNoteField | null;
  /** Whether Submit charges a card, which is what the agreement has to say out loud. */
  isPayNow: boolean;
  /** Prepaid credit covers the whole order, so Submit charges no card. */
  paysWithCredit: boolean;
  /** The fee and tax a card is charged on top of `totalCents`; null when the cart bills a contract. */
  charges: CartCharges | null;
  /** Provider licenses the cart's scenes come with, which submitting agrees to. */
  licenses: readonly OrderLicense[];
  onSubmit: () => void;
}

const UNPRICED_VALUES: Record<Exclude<CartCharges['status'], 'ready'>, string> = {
  loading: 'Calculating…',
  estimated: 'At checkout',
  failed: '—',
};

/** The formatted amount once Stripe has priced the cart, and what to show until then. */
const chargeValue = (
  charges: CartCharges,
  pick: (ready: CartCharges & {status: 'ready'}) => number
) => (charges.status === 'ready' ? formatCost(pick(charges)) : UNPRICED_VALUES[charges.status]);

/** What the order costs, and the button that places it. */
export const OrderSummarySidebar: React.FC<OrderSummarySidebarProps> = ({
  itemCount,
  totalCents,
  totalAcres,
  isSubmitting,
  submitBlock,
  paymentSection,
  billingNote,
  isPayNow,
  paysWithCredit,
  charges,
  licenses,
  onSubmit,
}) => (
  <aside className={cs.sidebar} aria-labelledby="cart-order-summary-heading">
    <header className={cs.header}>
      <h2 id="cart-order-summary-heading">Order summary</h2>
    </header>

    <div className={cs.body}>
      <div className={cs.metrics}>
        <div className={cs.metric}>
          <span>Scenes</span>
          <span className={cs.metricValue}>{itemCount}</span>
        </div>
        <div className={cs.metric}>
          <span>Total acres</span>
          <span className={cs.metricValue}>{conversionUtils.numberWithCommas(totalAcres)}</span>
        </div>
        {charges !== null && (
          <>
            <div className={cs.metric}>
              <span>Subtotal</span>
              <span className={cs.metricValue}>{formatCost(totalCents)}</span>
            </div>
            {charges.status === 'ready' && charges.prepaidCents > 0 && (
              <div className={cs.metric}>
                <span>Prepaid credit</span>
                <span className={cs.metricValue}>−{formatCost(charges.prepaidCents)}</span>
              </div>
            )}
            {charges.status === 'estimated' && charges.prepaidUpToCents > 0 && (
              <div className={cs.metric}>
                <span>Prepaid credit</span>
                <span className={cs.metricValue}>
                  up to −{formatCost(charges.prepaidUpToCents)}
                </span>
              </div>
            )}
            {charges.status === 'ready' && charges.orderMinimumCents > 0 && (
              <div className={cs.minimumOrder}>
                <div className={cs.metric}>
                  <span>Minimum order fee</span>
                  <span className={cs.metricValue}>{formatCost(charges.orderMinimumCents)}</span>
                </div>
                <p className={cs.minimumNote}>
                  Orders have a {formatCost(charges.orderMinimumThresholdCents)} minimum
                </p>
              </div>
            )}
            <div className={cs.metric}>
              <span>Service fee</span>
              <span className={cs.metricValue}>
                {chargeValue(charges, (ready) => ready.orderFeeCents)}
              </span>
            </div>
            <div className={cs.metric}>
              <span>Estimated tax</span>
              <span className={cs.metricValue}>
                {chargeValue(charges, (ready) => ready.taxCents)}
              </span>
            </div>
          </>
        )}
        <div className={cs.orderTotal}>
          <span className={cs.orderTotalLabel}>Order total</span>
          <span
            className={classnames(cs.orderTotalValue, {
              [cs.orderTotalAtCheckout]: charges?.status === 'estimated',
            })}
          >
            {charges === null
              ? formatCost(totalCents)
              : chargeValue(charges, (ready) => ready.totalCents)}
          </span>
        </div>
        {charges?.status === 'estimated' && (
          <p className={cs.minimumNote}>
            Your prepaid credit is applied first. Your card pays the rest at checkout.
          </p>
        )}
      </div>
    </div>

    <footer className={cs.actions}>
      {paymentSection}

      {billingNote && (
        <B.FormGroup
          label="Billing note"
          labelInfo="(optional)"
          labelFor={BILLING_NOTE_ID}
          helperText={
            billingNote.isLocked
              ? 'Kept for the checkout still in progress.'
              : 'Printed on your invoice, for your own records.'
          }
        >
          <B.InputGroup
            id={BILLING_NOTE_ID}
            value={billingNote.value}
            placeholder="Fund or PO number"
            maxLength={CHECKOUT_NOTE_MAX_LENGTH}
            disabled={billingNote.isLocked || isSubmitting}
            onChange={(event) => billingNote.onChange(event.currentTarget.value)}
          />
        </B.FormGroup>
      )}

      {submitBlock !== null &&
        (submitBlock.tone === 'progress' ? (
          // The spinner goes in the body, not the icon slot: Blueprint only lays out a real icon
          // beside the text, and a Spinner there breaks onto its own line.
          <B.Callout className={cs.blocked} icon={null}>
            <span className={cs.progress}>
              <B.Spinner size={14} />
              {submitBlock.message}
            </span>
          </B.Callout>
        ) : (
          <B.Callout className={cs.blocked} intent={B.Intent.DANGER} icon="warning-sign">
            {submitBlock.message}
          </B.Callout>
        ))}

      <B.Callout className={cs.agreement} icon={null} intent={B.Intent.WARNING}>
        {paysWithCredit ? (
          <>
            Your prepaid credit covers this order, so no card is charged. Orders cannot be refunded
            after purchase.
          </>
        ) : isPayNow && charges?.status === 'estimated' ? (
          <>
            By clicking “Submit order,” you authorize a charge for whatever your prepaid credit
            doesn’t cover. Orders cannot be refunded after purchase.
          </>
        ) : isPayNow ? (
          <>
            By clicking “Submit order,” you authorize a charge of{' '}
            {charges?.status === 'ready' ? formatCost(charges.totalCents) : 'the order total'}.
            Orders cannot be refunded after purchase.
          </>
        ) : (
          <>
            By clicking “Submit order,” you agree to this charge. Orders cannot be refunded after
            purchase.
          </>
        )}
        {licenses.length > 0 && (
          <>
            {' '}
            By submitting this order, you agree to comply with the{' '}
            {licenses.map((license, index) => (
              <React.Fragment key={license.url}>
                {index === 0 ? null : index === licenses.length - 1 ? ' and the ' : ', the '}
                <a href={license.url} target="_blank" rel="noopener noreferrer">
                  {license.name}
                </a>
              </React.Fragment>
            ))}
            .
          </>
        )}
      </B.Callout>

      <B.Button
        large
        fill
        intent={B.Intent.PRIMARY}
        loading={isSubmitting}
        disabled={submitBlock !== null}
        onClick={onSubmit}
      >
        Submit order
      </B.Button>
    </footer>
  </aside>
);

export default OrderSummarySidebar;
